/* eslint-disable max-lines */
import React, { useEffect, useMemo } from 'react';

import useTimezone from 'lib/hooks/useTimezone';
import { cn, ia, iaRa, inRn } from 'lib/helpers/utility';
import useRetainedValue from 'lib/hooks/useRetainedValue';
import { useTableContext } from 'lib/context/TableContext/TableContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';

import AGTable from '../AGTable/AGTable';
import { CustomStatusBarCount } from '../AGTable/CustomStatusBarCount';
import { CustomStatusBarPagination } from '../AGTable/CustomStatusBarPagination';
import CustomStatusBarAggregationComponent from '../AGTable/CustomStatusBarAggregationComponent';

import TableHeader from './components/TableHeader';
import TableFilters from './components/TableFilters';
import { createQueryFilters } from './lib/createQueryFilters';
import { useTableAutoHeight } from './hooks/useTableAutoHeight';
import { RowSelectionActions } from './components/RowSelectionActions';
import {
  DEFAULT_COLUMN_DEFS,
  DEFAULT_PAGE_COUNT,
  DEFAULT_STALE_TIME,
  GRID_OPTIONS
} from './lib/constants';

const MODAL_CLOSE_MS = 300;

const AgTablePage = (props) => {
  const {
    defaultFilters = {},
    defaultColumns,
    name,
    pagination = true,
    getExportingData,
    exportParams,
    ...rest
  } = props;

  return (
    <TableContextProvider
      defaultFilters={defaultFilters}
      cols={defaultColumns}
      name={name}
      pagination={pagination}
      getData={getExportingData}
      exportParams={exportParams}
      {...rest}>
      <AgTablePageContent {...rest} />
    </TableContextProvider>
  );
};

const AgTablePageContent = ({
  useData,
  queryParams = {},
  queryOptions = {},
  ModalComponent,
  subtitle,
  onCellClicked,
  onSelectRow,
  headerActions,
  refresh = false,
  skeletonSize,
  noTitle,
  showExport,
  switchItems,
  title,
  rightHeaderActions,
  noNewButton,
  actions,
  noHeader,
  formatData,
  queryKey,
  newButtonText,
  disabledCells = [],
  type,
  dependencies,
  noDisplayButton = false,
  noFilterActions = false,
  hideStatusBar = false,
  CustomPagination,
  CustomCount,
  fetchOnlyEnabledColumns = false,
  selectedRowIds,
  extraColumns = [],
  className,
  disableActions,
  ...rest
}) => {
  const {
    name,
    limit,
    pagination,
    filters,
    changeFilters,
    updateFilters,
    defaultFilters,
    initialFilters,
    errors,
    forceRefresh,
    isEditingFilter,
    selectedItem,
    setSelectedItem,
    showModal,
    setShowModal,
    filtersInitialized,
    sortField,
    sort,
    view,
    page,
    colDefs,
    columnsReady,
    gridApi,
    gridWrapperRef
  } = useTableContext();

  const timezone = useTimezone();

  const isSwitchItemView = useMemo(() => {
    return switchItems?.some((item) => item.value === view);
  }, [switchItems, view]);

  const enabledColumns = useMemo(() => {
    if (!fetchOnlyEnabledColumns || !ia(colDefs)) return;
    const columns = colDefs.filter((col) => !col.hide && col.field).map((col) => col.field);
    return [...columns, ...extraColumns];
  }, [colDefs, fetchOnlyEnabledColumns]);

  const queryFilters = useMemo(() => {
    if (!filtersInitialized) return {};
    const result = createQueryFilters(filters, isSwitchItemView && view, timezone);
    return result;
  }, [filters, view, isSwitchItemView, filtersInitialized, timezone]);

  const queryFiltersKey = useMemo(() => JSON.stringify(queryFilters), [queryFilters]);

  const newDependencies = useMemo(() => {
    const baseDeps = [
      limit,
      page,
      sort,
      queryFiltersKey,
      view,
      sortField,
      ...(fetchOnlyEnabledColumns && ia(enabledColumns) ? [enabledColumns.join(',')] : [])
    ];
    if (ia(dependencies)) {
      return [...baseDeps, ...dependencies];
    }
    return [...baseDeps, ...Object.values(queryParams)];
  }, [
    limit,
    page,
    sort,
    queryFiltersKey,
    queryParams,
    view,
    dependencies,
    sortField,
    enabledColumns
  ]);

  const { data, refetch, isLoading, isRefetching } = useData({
    name,
    params: {
      limit,
      page,
      ...(sortField && { sort: sortField }),
      filters: queryFilters,
      withCount: true,
      ...(fetchOnlyEnabledColumns && { enabledColumns }),
      ...queryParams,
      ...(isSwitchItemView && view != null && { view })
    },
    options: {
      staleTime: DEFAULT_STALE_TIME,
      enabled: filtersInitialized && (!fetchOnlyEnabledColumns || columnsReady),
      ...queryOptions
    },
    dependencies: newDependencies
  });

  const loading = !filtersInitialized || isLoading;
  const transitioning = isRefetching && !isLoading;

  const items = useMemo(() => {
    if (loading) return;
    if (queryKey === 'direct.format' && formatData) {
      return formatData(data);
    } else {
      return formatData ? formatData(iaRa(data)) : iaRa(data?.[queryKey || name]);
    }
  }, [data, name, loading, formatData, queryKey]);

  const hasActiveFilters = Object.values(filters || {}).some((v) => ia(v));

  const count = inRn(data?.count) ?? DEFAULT_PAGE_COUNT;
  const statusPanelData = useMemo(() => data?.statusPanelData, [data]);

  const { isSinglePage, isAutoHeight } = useTableAutoHeight({ count });

  const serializedSelectedRowIds = useMemo(
    () => JSON.stringify(selectedRowIds || []),
    [selectedRowIds]
  );

  useEffect(() => {
    if (!ia(selectedRowIds) || !gridApi || loading) return;

    gridApi.deselectAll();

    const ids = selectedRowIds.filter((id) => id != null && id !== '');
    if (!ia(ids)) return;

    const idSet = new Set(ids.map(String));

    gridApi.forEachNode((node) => {
      const rowId = node.data?.id ?? node.data?.value;
      if (rowId != null && idSet.has(String(rowId))) {
        node.setSelected(true, false);
      }
    });
  }, [gridApi, loading, serializedSelectedRowIds]);

  const onCellClickedHandler = (event) => {
    if (loading || transitioning) return;

    if (['actions', '0', ...disabledCells].includes(event?.column?.colId) || event?.node?.group)
      return;

    if (typeof onCellClicked === 'function') {
      onCellClicked(event);
      return;
    }

    if (typeof onSelectRow === 'function') {
      onSelectRow(event?.data, event);
      return;
    }
    setSelectedItem(event?.data);
    setShowModal(true);
  };

  const onModalToggle = () => {
    setShowModal((prev) => !prev);
    setSelectedItem(null);
  };

  const retainedSelectedItem = useRetainedValue(selectedItem, {
    releaseAfterMs: MODAL_CLOSE_MS
  });

  const statusBar = useMemo(() => {
    if (hideStatusBar || (isSinglePage && !ia(statusPanelData))) return null;

    return {
      statusPanels: [
        {
          statusPanel: CustomCount ? CustomCount : CustomStatusBarCount,
          statusPanelParams: CustomCount ? { data } : { count, pagination },
          align: 'left'
        },
        ...(pagination
          ? [
              {
                statusPanel: CustomPagination ? CustomPagination : CustomStatusBarPagination,
                statusPanelParams: CustomPagination ? { data } : { count },
                align: 'right'
              }
            ]
          : []),
        ...(statusPanelData
          ? [
              {
                statusPanel: CustomStatusBarAggregationComponent,
                statusPanelParams: {
                  statusPanelData
                },
                align: 'center'
              }
            ]
          : [])
      ]
    };
  }, [count, statusPanelData, pagination]);

  return (
    <div ref={gridWrapperRef} className={cn('flex h-full min-h-0 flex-col', className)}>
      {!noHeader && (
        <TableHeader
          category={name}
          onModalToggle={onModalToggle}
          hasNew={!!ModalComponent && !noNewButton}
          subtitle={subtitle}
          actions={headerActions}
          refresh={refresh}
          onRefresh={refetch}
          noTitle={noTitle}
          showExport={showExport}
          switchItems={switchItems}
          title={title}
          count={count}
          rightActions={rightHeaderActions}
          loading={loading}
          newButtonText={newButtonText}
          statusPanelData={statusPanelData}
          data={data}
        />
      )}
      <TableFilters
        filters={filters}
        initialFilters={initialFilters}
        defaultFilters={defaultFilters}
        changeFilters={changeFilters}
        updateFilters={updateFilters}
        category={name}
        isEditingFilter={isEditingFilter}
        items={switchItems}
        type={type}
        noDisplayButton={noDisplayButton}
        noFilterActions={noFilterActions}
        hasData={ia(items) || hasActiveFilters || !!filters?.search?.searchTerm}
        itemsCount={items?.length}
        disableActions={disableActions}
      />
      <div
        className={cn(
          'relative flex flex-col overflow-hidden',
          isAutoHeight ? 'h-auto' : 'h-full flex-1'
        )}>
        <div
          className={cn(
            'ag-theme-quartz transition-opacity duration-200',
            isAutoHeight ? 'h-auto' : 'h-full flex-1',
            transitioning && 'pointer-events-none opacity-50'
          )}>
          <AGTable
            key={forceRefresh}
            data={items}
            gridOptions={GRID_OPTIONS}
            defaultColDef={DEFAULT_COLUMN_DEFS}
            onCellClicked={onCellClickedHandler}
            statusBar={statusBar}
            loading={loading}
            skeletonSize={skeletonSize}
            count={count}
            {...rest}
          />
        </div>
        <RowSelectionActions actions={actions} loading={loading} />
      </div>
      {ModalComponent && (
        <ModalComponent
          item={retainedSelectedItem}
          isOpen={!!showModal}
          handleClose={onModalToggle}
          filters={filters}
          errors={errors}
          category={name}
          tableData={items}
        />
      )}
    </div>
  );
};

export default AgTablePage;
